import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  AddBusiness as AddBusinessIcon,
  ArrowForward as ArrowForwardIcon,
  Badge as BadgeIcon,
  Handshake as HandshakeIcon,
  People as PeopleIcon,
  ReceiptLong as ReceiptLongIcon,
  RocketLaunch as RocketLaunchIcon,
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PartnerLayout from '../../components/partner/PartnerLayout';
import PartnerHelpPanel from '../../components/partner/PartnerHelpPanel';
import { partnerHeroGradient } from '../../components/partner/partnerTheme';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import partnerApiService from '../../services/partnerApi';
import EmployeeSizePicker, {
  EmployeeSize, asSize
} from '../../components/EmployeeSizePicker';
import { PartnerClientSummary, PartnerStatement } from '../../types/partner';
import { firstDefined, formatCredits } from '../../utils/credits';

// Dashboard del PORTAL PARTNER: hero cálido con métricas de cartera (clientes,
// saldo mayorista, consumo del mes) y, si la cartera está vacía, ONBOARDING de
// 2 pasos con CTAs (perfil → crear primer cliente). El empleado NO es un
// paso guiado: lo contrata el cliente desde su portal, o el partner desde la ficha.

const PROFILE_STEP_KEY = 'partner_onboarding_profile_ok';

const PartnerDashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me, reseller, refreshMe } = usePartnerAuth();

  const [clients, setClients] = useState<PartnerClientSummary[]>([]);
  const [statement, setStatement] = useState<PartnerStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileOk, setProfileOk] = useState<boolean>(() => localStorage.getItem(PROFILE_STEP_KEY) === '1');

  const loadData = useCallback(async () => {
    try {
      const now = new Date();
      const [clientsData, statementData] = await Promise.allSettled([
        partnerApiService.getClients(),
        partnerApiService.getStatement(now.getFullYear(), now.getMonth() + 1)
      ]);
      if (clientsData.status === 'fulfilled') setClients(clientsData.value);
      // El statement puede no existir aún (backend en paralelo / mes sin datos):
      // el hero enseña «—» sin romper.
      if (statementData.status === 'fulfilled') setStatement(statementData.value);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalEmployees = clients.reduce((sum, c) => sum + (c.employees_count ?? 0), 0);
  const emptyPortfolio = clients.length === 0 || totalEmployees === 0;
  // allocated_total es el nombre real del backend; allocated queda como alias
  // histórico (el frontend nació antes que el backend del statement).
  const monthConsumption = statement
    ? firstDefined(statement.allocated_total, statement.allocated)
    : null;

  // «Tus condiciones»: el bloque comercial del partner, con el LÍMITE DE COMPRA
  // MENSUAL solo cuando existe (0 = sin límite por contrato). Lo comprado este
  // ciclo y la fecha de renovación son derivados de /partner/me.
  const discountPct = firstDefined(reseller?.wholesale_discount_pct);
  const wholesaleCap = Number(reseller?.max_wholesale_credits ?? 0);
  const wholesalePurchased = Number(reseller?.wholesale_purchased_this_cycle ?? 0);
  const wholesaleRenewsAt = reseller?.wholesale_cycle_renews_at ?? null;
  const wholesaleRenewsLabel = wholesaleRenewsAt
    ? new Date(wholesaleRenewsAt).toLocaleDateString(i18n.language || 'es')
    : null;

  // Cupo AGREGADO de empleados IA (todos sus clientes): 0 = sin límite. El
  // «usado» lo manda /partner/me ya contado; la suma local de employees_count
  // queda de respaldo por si el backend aún no expone el derivado.
  const employeesCap = Number(reseller?.max_total_employees ?? 0);
  const employeesUsed = typeof reseller?.employees_count === 'number'
    ? reseller.employees_count
    : totalEmployees;
  const employeesQuotaFull = employeesCap > 0 && employeesUsed >= employeesCap;

  // Techo AGREGADO de ALMACENAMIENTO (GiB de toda la cartera): la otra
  // dimensión de la carga que el partner mete en el cluster. El «usado» lo
  // manda /partner/me ya sumado; sin el dato (backend anterior a la migración
  // 038) la métrica no se pinta en vez de enseñar un 0 que parecería real.
  const storageUsed = typeof reseller?.storage_used_gib === 'number'
    ? reseller.storage_used_gib
    : null;
  const storageCap = Number(reseller?.max_total_storage_gib ?? 0);
  const storageQuotaFull = storageCap > 0 && storageUsed !== null && storageUsed >= storageCap;

  // FACTURACIÓN del almacenamiento: el disco de toda la cartera se le cobra al
  // PARTNER (sus clientes no pagan almacenamiento), así que aquí van los tres
  // números que explican el cargo — incluido, facturable y créditos por ciclo —
  // y lo que ya tiene repartido. Sin el dato (backend anterior a la migración
  // 039) la tarjeta no se pinta: mejor nada que un 0 que parecería real.
  const storageIncluded = typeof reseller?.included_storage_gib === 'number'
    ? reseller.included_storage_gib
    : null;
  const storageBillable = typeof reseller?.storage_billable_gib === 'number'
    ? reseller.storage_billable_gib
    : null;
  const storageCycleCredits = firstDefined(reseller?.storage_cycle_credits);
  const storageAllocated = typeof reseller?.storage_allocated_gib === 'number'
    ? reseller.storage_allocated_gib
    : null;
  const storageWithoutCap = Number(reseller?.clients_without_storage_cap ?? 0);
  const storageRenewsAt = reseller?.storage_cycle_renews_at || null;
  const showStorageCard = storageUsed !== null && storageIncluded !== null;

  // TALLA por defecto de la cartera: la que saldrá marcada al contratar. Se
  // guarda al pulsar, sin diálogo: es una preferencia reversible que no toca
  // nada desplegado — pedir confirmación para eso sería teatro.
  const [savingSize, setSavingSize] = useState(false);
  const defaultSize = asSize(reseller?.default_employee_size);
  const handleDefaultSize = async (next: EmployeeSize) => {
    if (next === defaultSize || savingSize) return;
    try {
      setSavingSize(true);
      await partnerApiService.setDefaultEmployeeSize(next);
      await refreshMe();
    } catch (err) {
      console.error('No se pudo guardar la talla por defecto:', err);
    } finally {
      setSavingSize(false);
    }
  };

  // Paso activo del onboarding: perfil → primer cliente → primer empleado.
  const activeStep = !profileOk ? 0 : clients.length === 0 ? 1 : 2;

  // Estado de la cuenta traducido; cualquier estado nuevo del backend se
  // enseña tal cual en vez de quedarse en blanco.
  const statusLabel = (status: string) =>
    status === 'active'
      ? t('partner.clients.statusActive', 'Activa')
      : status === 'suspended'
        ? t('partner.clients.statusSuspended', 'Suspendida')
        : status;

  const acknowledgeProfile = () => {
    localStorage.setItem(PROFILE_STEP_KEY, '1');
    setProfileOk(true);
    setProfileDialogOpen(false);
  };

  // Tipada a mano: solo las métricas con cupo llevan `caption` (su ocupación)
  // y `warn` (si está al tope), y sin la anotación TS infiere un union sin
  // esos campos.
  const heroMetrics: {
    icon: React.ReactNode;
    label: string;
    value: string;
    caption?: string;
    warn?: boolean;
  }[] = [
    {
      icon: <PeopleIcon />,
      label: t('partner.dashboard.metricClients', 'Clientes'),
      value: `${clients.length}${typeof reseller?.max_clients === 'number' ? ` / ${reseller.max_clients}` : ''}`
    },
    {
      icon: <WalletIcon />,
      label: t('partner.dashboard.metricWholesale', 'Saldo mayorista'),
      value: formatCredits(reseller?.wholesale_balance)
    },
    {
      icon: <TrendingUpIcon />,
      label: t('partner.dashboard.metricMonthConsumption', 'Consumo del mes'),
      value: monthConsumption === null || monthConsumption === undefined ? '—' : formatCredits(monthConsumption)
    },
    {
      icon: <SmartToyIcon />,
      label: t('partner.dashboard.metricEmployees', 'Empleados IA activos'),
      // Con cupo se enseña «X de Y»: el partner ve su margen de crecimiento
      // antes de prometerle un empleado más a un cliente.
      value: employeesCap > 0
        ? t('partner.dashboard.metricEmployeesOfCap', '{{used}} de {{max}}',
            { used: employeesUsed, max: employeesCap })
        : String(employeesUsed),
      caption: employeesCap > 0
        ? (employeesQuotaFull
            ? t('partner.dashboard.employeesQuotaFull',
                'Límite alcanzado: retira alguno o pide ampliación')
            // `count` (no `left`): con uno solo el texto va en singular.
            : t('partner.dashboard.employeesQuotaLeft', 'Te quedan {{count}}',
                { count: employeesCap - employeesUsed }))
        : undefined,
      warn: employeesQuotaFull
    },
    // Almacenamiento agregado: solo si el backend lo informa.
    ...(storageUsed === null
      ? []
      : [{
          icon: <StorageIcon />,
          label: t('partner.dashboard.metricStorage', 'Almacenamiento'),
          value: storageCap > 0
            ? t('partner.dashboard.metricStorageOfCap', '{{used}} de {{max}} GiB',
                { used: storageUsed, max: storageCap })
            : t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: storageUsed }),
          caption: storageCap > 0
            ? (storageQuotaFull
                ? t('partner.dashboard.storageQuotaFull',
                    'Límite alcanzado: retira empleados o pide ampliación')
                : t('partner.dashboard.storageQuotaLeft', 'Te quedan {{gib}} GiB',
                    { gib: storageCap - storageUsed }))
            : t('partner.dashboard.storageNoCap', 'Sin límite de almacenamiento'),
          warn: storageQuotaFull
        }])
  ];

  return (
    <PartnerLayout>
      {/* Hero cálido con métricas de cartera */}
      <Box sx={{ background: partnerHeroGradient, color: '#fff', pb: 10 }}>
        <Container maxWidth="lg" sx={{ pt: 6 }}>
          <Typography variant="h3" sx={{ mb: 1, fontSize: { xs: '1.9rem', md: '2.6rem' } }}>
            {t('partner.dashboard.greeting', 'Hola, {{name}}', {
              name: reseller?.name || me?.user?.full_name || t('partner.dashboard.greetingFallback', 'partner')
            })}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, mb: 2 }}>
            {t('partner.dashboard.subtitle', 'Tu cartera de clientes y empleados IA, de un vistazo.')}
          </Typography>
          {/* Ayuda de ESTA pantalla + puerta de entrada a la guía completa. En
              el panel es donde más falta hace: es la primera pantalla que ve un
              partner nuevo y la que más cifras sin explicar tiene. */}
          <Box sx={{ mb: 4, '& .MuiButton-root': { color: '#fff' } }}>
            <PartnerHelpPanel screen="dashboard" />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -8, pb: 6 }}>
        {/* Tarjetas grandes de métricas, solapadas sobre el hero */}
        <Grid container spacing={3} mb={4}>
          {heroMetrics.map((metric) => (
            <Grid item xs={12} sm={6} md={heroMetrics.length > 4 ? 4 : 3} key={metric.label}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ py: 3 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5} color="primary.main">
                    {metric.icon}
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                      {metric.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {loading ? <CircularProgress size={24} /> : metric.value}
                  </Typography>
                  {!loading && metric.caption && (
                    <Typography
                      variant="caption"
                      color={metric.warn ? 'warning.main' : 'text.secondary'}
                      sx={{ fontWeight: 600 }}
                    >
                      {metric.caption}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* «Tus condiciones»: el trato comercial del partner, siempre visible
            — descuento (su margen), cupo de clientes y techo de bolsa si lo hay. */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1.5} color="primary.main">
              <HandshakeIcon />
              <Typography variant="h6" color="text.primary">
                {t('partner.conditions.title', 'Tus condiciones')}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {t('partner.conditions.discount', 'Descuento mayorista')}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {discountPct === null ? '—' : `${Number(discountPct)} %`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  {t('partner.conditions.maxClients', 'Máx. clientes')}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {typeof reseller?.max_clients === 'number' ? reseller.max_clients : '—'}
                </Typography>
              </Grid>
              {employeesCap > 0 && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.conditions.maxEmployees', 'Máx. empleados IA (toda tu cartera)')}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {t('partner.dashboard.metricEmployeesOfCap', '{{used}} de {{max}}',
                      { used: employeesUsed, max: employeesCap })}
                  </Typography>
                </Grid>
              )}
              {wholesaleCap > 0 && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.conditions.wholesaleCap', 'Límite de compra mensual')}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {t('partner.conditions.wholesaleCapValue', '{{purchased}} de {{max}}', {
                      purchased: formatCredits(wholesalePurchased),
                      max: formatCredits(wholesaleCap)
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {wholesaleRenewsLabel
                      ? t('partner.conditions.wholesaleCapRenews', 'comprado este mes · se renueva el {{date}}', { date: wholesaleRenewsLabel })
                      : t('partner.conditions.wholesaleCapUsed', 'comprado este mes')}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Typography variant="body2" color="text.secondary" mt={2}>
              {t(
                'partner.conditions.explainer',
                'Compras créditos a precio de lista menos tu descuento: ese descuento es tu margen. El marketplace te factura mensualmente tus compras con el descuento aplicado; el precio al que asignas créditos a tus clientes lo decides tú.'
              )}
            </Typography>
          </CardContent>
        </Card>

        {/* Almacenamiento de la cartera: lo que ocupa, lo que el marketplace
            le incluye y lo que le cuesta el exceso POR CICLO. Va aparte de la
            métrica del hero porque no es solo ocupación: es una factura, y el
            partner tiene que poder cuadrarla sin abrir su ledger. */}
        {showStorageCard && (
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1.5} color="primary.main">
                <StorageIcon />
                <Typography variant="h6" color="text.primary">
                  {t('partner.storage.title', 'Almacenamiento de tu cartera')}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.storage.used', 'En uso')}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {storageCap > 0
                      ? t('partner.dashboard.metricStorageOfCap', '{{used}} de {{max}} GiB',
                          { used: storageUsed, max: storageCap })
                      : t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: storageUsed })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.storage.included', 'Incluido sin coste')}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: storageIncluded })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.storage.billable', 'Facturable')}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    color={storageBillable && storageBillable > 0 ? 'warning.main' : 'text.primary'}
                  >
                    {t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: storageBillable ?? 0 })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.storage.cycleCost', 'Coste por ciclo')}
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {storageCycleCredits === null || storageCycleCredits === undefined
                      ? '—'
                      : t('partner.storage.creditsPerCycle', '{{credits}} créditos',
                          { credits: formatCredits(storageCycleCredits) })}
                  </Typography>
                  {storageRenewsAt && (
                    <Typography variant="caption" color="text.secondary">
                      {t('partner.storage.nextCharge', 'Próximo cargo: {{date}}', {
                        date: new Date(storageRenewsAt).toLocaleDateString(i18n.language || 'es')
                      })}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              {storageAllocated !== null && storageCap > 0 && (
                <Typography variant="body2" color="text.secondary" mt={2}>
                  {t('partner.storage.allocated', 'Repartes {{allocated}} de {{max}} GiB entre tus clientes.', {
                    allocated: storageAllocated,
                    max: storageCap
                  })}
                  {storageWithoutCap > 0 && ' ' + t(
                    'partner.storage.withoutCap',
                    '{{count}} cliente(s) sin techo propio: cuentan por lo que ocupan hoy.',
                    { count: storageWithoutCap }
                  )}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" mt={storageAllocated !== null && storageCap > 0 ? 1 : 2}>
                {t(
                  'partner.storage.explainer',
                  'El almacenamiento de tus clientes te lo factura el marketplace a ti, no a ellos: pagas por ciclo, con créditos de tu bolsa, lo que tu cartera ocupe por encima de lo incluido. Tus clientes no ven ningún coste de almacenamiento — el reparto lo decides tú desde el detalle de cada cliente.'
                )}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* TALLA por defecto de la cartera. Va aquí, junto al almacenamiento,
            porque son las dos decisiones de capacidad que el partner toma una
            vez y se aplican a todo lo que contrate después. */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1.5} color="primary.main">
              <SmartToyIcon />
              <Typography variant="h6" color="text.primary">
                {t('partner.defaultSize.title', 'Capacidad por defecto de tus empleados')}
              </Typography>
            </Box>
            <EmployeeSizePicker
              value={defaultSize}
              onChange={handleDefaultSize}
              disabled={savingSize}
            />
            <Typography variant="body2" color="text.secondary">
              {t('partner.defaultSize.explainer',
                'Es la que aparecerá marcada al contratar empleados nuevos en tu cartera; quien contrate puede elegir la otra. Cambiarla no afecta a los empleados que ya están en marcha: para cambiarles la capacidad hay que volver a desplegarlos.')}
            </Typography>
          </CardContent>
        </Card>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : emptyPortfolio ? (
          /* ── ONBOARDING de 2 pasos (cartera vacía) ── */
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <RocketLaunchIcon color="primary" fontSize="large" />
                <Typography variant="h5">
                  {t('partner.onboarding.title', 'Pon en marcha tu cartera en 2 pasos')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {t('partner.onboarding.subtitle', 'Crea clientes y reparte créditos desde tu bolsa mayorista. Los empleados IA los contrata tu cliente desde su portal (o puedes hacerlo tú desde su ficha). La facturación es mensual, sin pasarela.')}
              </Typography>
              <Stepper activeStep={activeStep} orientation="vertical">
                <Step completed={profileOk}>
                  <StepLabel>
                    <Typography fontWeight={700}>
                      {t('partner.onboarding.step1Title', 'Revisa tu perfil de partner')}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {t('partner.onboarding.step1Text', 'Comprueba tu bolsa mayorista, tu descuento y el cupo de clientes que puedes gestionar.')}
                    </Typography>
                    <Button variant="contained" startIcon={<BadgeIcon />} onClick={() => setProfileDialogOpen(true)}>
                      {t('partner.onboarding.step1Cta', 'Ver mi perfil')}
                    </Button>
                  </StepContent>
                </Step>
                <Step completed={clients.length > 0}>
                  <StepLabel>
                    <Typography fontWeight={700}>
                      {t('partner.onboarding.step2Title', 'Crea tu primer cliente')}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {t('partner.onboarding.step2Text', 'Da de alta la cuenta de tu cliente con el email de su propietario: la contraseña generada se enseña una sola vez.')}
                    </Typography>
                    <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => navigate('/partner/clients?new=1')}>
                      {t('partner.onboarding.step2Cta', 'Crear cliente')}
                    </Button>
                  </StepContent>
                </Step>
              </Stepper>
            </CardContent>
          </Card>
        ) : (
          /* ── Cartera con actividad: resumen de clientes ── */
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h5">
                {t('partner.dashboard.portfolio', 'Tu cartera')}
              </Typography>
              <Box display="flex" gap={1.5}>
                <Button variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => navigate('/partner/billing')}>
                  {t('partner.dashboard.goBilling', 'Ver facturación')}
                </Button>
                <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => navigate('/partner/clients?new=1')}>
                  {t('partner.dashboard.newClient', 'Crear cliente')}
                </Button>
              </Box>
            </Box>
            <Grid container spacing={3}>
              {clients.map((client) => (
                <Grid item xs={12} sm={6} md={4} key={client.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h6">{client.name}</Typography>
                        <Chip
                          label={statusLabel(client.status)}
                          color={client.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      {client.company && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {client.company}
                        </Typography>
                      )}
                      <Box display="flex" gap={3} mt={1.5}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('partner.clients.employees', 'Empleados')}
                          </Typography>
                          <Typography variant="body1" fontWeight={700}>
                            {client.employees_count ?? 0}
                            {typeof client.max_employees === 'number' ? ` / ${client.max_employees}` : ''}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('partner.clients.balance', 'Saldo')}
                          </Typography>
                          <Typography variant="body1" fontWeight={700}>
                            {formatCredits(client.balance)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    <Box px={2} pb={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(`/partner/clients/${client.id}`)}
                      >
                        {t('partner.clients.viewDetail', 'Ver detalle')}
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      {/* Diálogo «Mi perfil de partner» (paso 1 del onboarding) */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('partner.profile.title', 'Mi perfil de partner')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('partner.profile.name', 'Nombre')}
              </Typography>
              <Typography variant="body1" fontWeight={700}>{reseller?.name || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('partner.profile.wholesaleBalance', 'Bolsa mayorista')}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {formatCredits(reseller?.wholesale_balance)} {t('partner.profile.creditsUnit', 'créditos')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('partner.profile.discount', 'Descuento mayorista')}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {reseller?.wholesale_discount_pct === null || reseller?.wholesale_discount_pct === undefined
                  ? '—'
                  : `${Number(reseller.wholesale_discount_pct)}%`}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('partner.profile.maxClients', 'Cupo de clientes')}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {typeof reseller?.max_clients === 'number' ? reseller.max_clients : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('partner.profile.maxEmployees', 'Cupo de empleados IA (toda tu cartera)')}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {employeesCap > 0
                  ? t('partner.dashboard.metricEmployeesOfCap', '{{used}} de {{max}}',
                      { used: employeesUsed, max: employeesCap })
                  : t('partner.profile.noEmployeeCap', 'Sin límite')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>{t('common.close', 'Cerrar')}</Button>
          <Button variant="contained" onClick={acknowledgeProfile}>
            {t('partner.profile.confirm', 'Todo correcto')}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerLayout>
  );
};

export default PartnerDashboardPage;
